import { useState, useEffect } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Save, ArrowLeft, MapPin, Files, Edit, WifiOff } from 'lucide-react';
import { GenericListManager } from '@/components/ui/generic-list-manager';


import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '@/components/ui/breadcrumb';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import MapboxMap, { parseCoords } from '@/ubication/mapbox';
import { toast } from 'react-toastify';
import { SignatureCapture } from '@/lib/utils/signatureCapture';
import { RequestVisitService } from '@/lib/collections/VisitsLogs/FetchVisitLogs';
import { offlineDB } from '@/lib/offline/offlineDB';
import { useOnlineStatus } from '@/hooks/offline/useOnlineStatus';

interface AddVisitlogProps {
    visitId: string;
    userId: string;
}

const AddVisitlog = ({ visitId, userId }: AddVisitlogProps) => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const [document, setDocument] = useState<any>(null);
    const [_companyName, setCompanyName] = useState('');
    const [contactPerson, setContactPerson] = useState('');
    const [contactRole, setContactRole] = useState('');
    const [_visitPurpose, setVisitPurpose] = useState('');
    const [agreements, setAgreements] = useState<string[]>([]);
    const [observations, setObservations] = useState<string[]>([]);

    const [signaturePreview, setSignaturePreview] = useState<string>('');
    const [hasNewSignature, setHasNewSignature] = useState(false);

    const [_startDate, setStartDate] = useState<Date | undefined>(undefined);
    const [_startTime, setStartTime] = useState<string>('');
    const [_endDate, setEndDate] = useState<Date | undefined>(undefined);
    const [_endTime, setEndTime] = useState<string>('');

    const isOnline = useOnlineStatus(); //offline

    useEffect(() => {
        loadDocument();
    }, [visitId]);



    //auto guardar si hay una nueva firma Y ya pasó el loading inicial
    useEffect(() => {
        if (hasNewSignature && signaturePreview && !loading) {
            handleUpdate();
        }
    }, [hasNewSignature, signaturePreview]);


    const loadDocument = async () => {
        try {
            setLoading(true);
            const doc = await RequestVisitService.getRequestVisitById(visitId);
            setDocument(doc);

            setCompanyName(doc.companyName || '');
            setContactPerson(doc.contactPerson || '');
            setContactRole(doc.contactRole || '');
            setVisitPurpose(doc.visitPurpose || '');
            setAgreements(doc.agreements || []);
            setObservations(doc.observations || []);

            if (doc.visits && doc.visits.length >= 3) {
                const startDateTime = new Date(doc.visits[1]);
                const endDateTime = new Date(doc.visits[2]);

                setStartDate(startDateTime);
                setStartTime(startDateTime.toTimeString().slice(0, 8));
                setEndDate(endDateTime);
                setEndTime(endDateTime.toTimeString().slice(0, 8));
            }
        } catch (error: any) {
        } finally {
            setLoading(false);
        }
    };

    const handleUpdate = async () => {
        if (!contactPerson.trim()) {
            toast.warning('El campo de la persona que recibe es obligatoria');
            return;
        }

        let toastId: any = null;

        try {
            setSaving(true);
            toastId = toast.loading('Guardando bitácora...');

            const logData: any = {
                contactPerson: contactPerson.trim(),
                contactRole: contactRole.trim(),
                agreements,
                observations,
            };

            //  MANEJO DE FIRMA (ONLINE Y OFFLINE)
            if (hasNewSignature && signaturePreview) {
                // Intentar obtener ubicación
                let coordinates: { lat: number; lng: number } | null = null;
                try {
                    const locationPromise = RequestVisitService.getCurrentLocation();
                    const timeoutPromise = new Promise((_, reject) =>
                        setTimeout(() => reject(new Error('Timeout')), 10000)
                    );

                    coordinates = await Promise.race([locationPromise, timeoutPromise]) as { lat: number; lng: number };
                } catch (locationError) {
                    console.warn('No se pudo obtener ubicación:', locationError);
                    // Continuar sin ubicación si está offline
                    if (!navigator.onLine) {
                        coordinates = null;
                    } else {
                        if (toastId !== null) toast.dismiss(toastId);
                        toast.error('No se pudo obtener tu ubicación. Por favor, enciende el GPS y vuelve a intentar.');
                        setSaving(false);
                        return;
                    }
                }

                //  VERIFICAR SI ESTÁ OFFLINE
                if (!navigator.onLine) {
                    // GUARDAR EN INDEXEDDB
                    await offlineDB.savePendingLog({
                        visitId,
                        userId,
                        contactPerson: contactPerson.trim(),
                        contactRole: contactRole.trim(),
                        agreements,
                        observations,
                        signatureDataURL: signaturePreview,
                        location: coordinates,
                    });

                    if (toastId !== null) toast.dismiss(toastId);
                    toast.success(' Bitácora guardada localmente. Se sincronizará cuando haya conexión.', {
                        autoClose: 5000,
                        icon: <WifiOff className="h-5 w-5" />
                    });

                    setHasNewSignature(false);
                    setSignaturePreview('');
                    setSaving(false);
                    return;
                }

                // MODO ONLINE 
                const signatureBlob = await fetch(signaturePreview).then(r => r.blob());
                const newSignatureFileId = await RequestVisitService.uploadSignature(signatureBlob);

                logData.signature = [
                    newSignatureFileId,
                    coordinates ? `${coordinates.lat},${coordinates.lng}` : '',
                    new Date().toISOString()
                ];
            } else if (document.signature && document.signature.length > 0) {
                logData.signature = document.signature;
            } else {
                logData.signature = [];
            }

            //  INTENTAR GUARDAR ONLINE
            try {
                await RequestVisitService.createVisitLog(visitId, logData);

                setHasNewSignature(false);
                setSignaturePreview('');
                await loadDocument();

                const isComplete =
                    contactPerson.trim() &&
                    contactRole.trim() &&
                    logData.signature.length > 0;

                if (toastId !== null) toast.dismiss(toastId);

                if (isComplete) {
                    toast.success("¡Bitácora completada exitosamente!");
                } else {
                    toast.info("Progreso guardado. Completa todos los campos para marcar como completado");
                }
            } catch (error: any) {
                // Si falló por estar offline
                if (error.message === 'OFFLINE_MODE') {
                    // Guardar en IndexedDB
                    await offlineDB.savePendingLog({
                        visitId,
                        userId,
                        contactPerson: contactPerson.trim(),
                        contactRole: contactRole.trim(),
                        agreements,
                        observations,
                        signatureDataURL: signaturePreview,
                        location: logData.signature[1] ? {
                            lat: parseFloat(logData.signature[1].split(',')[0]),
                            lng: parseFloat(logData.signature[1].split(',')[1])
                        } : null,
                    });

                    if (toastId !== null) toast.dismiss(toastId);
                    toast.success(' Sin conexión. Bitácora guardada localmente.', {
                        autoClose: 5000,
                    });
                } else {
                    throw error;
                }
            }

        } catch (error: any) {
            if (toastId !== null) toast.dismiss(toastId);
            toast.error("Error al guardar la bitácora. Intenta de nuevo.");
        } finally {
            setSaving(false);
            if (toastId !== null) toast.dismiss(toastId);
        }
    };

    // const handleDelete = async () => {
    //     try {
    //         await RequestVisitService.deleteRequestVisit(visitId);
    //         navigate({ to: `/requests/${userId}/visit` });
    //     } catch (error: any) {
    //         toast.error("Error al eliminar, " + err.message);
    //     }
    // };

    const parseVisits = (visits: string[]) => {
        try {
            return {
                registered: new Date(visits[0]),
                start: new Date(visits[1]),
                end: new Date(visits[2])
            };
        } catch {
            return null;
        }
    };

    const parseSignature = (signature: string[]) => {
        try {
            return {
                fileId: signature[0],
                coordinates: signature[1],
                timestamp: new Date(signature[2])
            };
        } catch {
            return null;
        }
    };


    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    if (!document) {
        return (
            <Alert variant="destructive">
                <AlertDescription>No se pudo cargar el documento</AlertDescription>
            </Alert>
        );
    }

    const visits = parseVisits(document.visits);
    const signature = parseSignature(document.signature);


    return (
        <>
            <Breadcrumb>
                <BreadcrumbList>
                    <BreadcrumbItem>
                        <div className="flex items-center gap-1.5">
                            <Files />
                            <BreadcrumbPage>Solicitudes</BreadcrumbPage>
                        </div>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                        <BreadcrumbLink asChild>
                            <Link to="/requests">Menu</Link>
                        </BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                        <BreadcrumbLink asChild>
                            <Link to="/visitLog/$id" params={{ id: userId! }}>
                                Registros
                            </Link>
                        </BreadcrumbLink>
                    </BreadcrumbItem>

                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                        <BreadcrumbPage className="hidden sm:block">Detalle de solicitud</BreadcrumbPage>
                        <BreadcrumbPage className="block sm:hidden">
                            {/* <File className="w-5 h-5" /> */}
                        </BreadcrumbPage>
                    </BreadcrumbItem>
                </BreadcrumbList>
            </Breadcrumb>

            <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8 font-outfit">
                <div className="max-w-8xl mx-auto">
                    <Card className="shadow-xl border-0 p-0">
                        <CardHeader className="relative text-white rounded-t-lg bg-purple-700 min-h-20 sm:h-20">
                            <div className="absolute top-0 rounded-t-4xl left-0 w-full h-full bg-purple-700"></div>
                            <div className="relative z-10 flex items-center gap-4 py-3">
                                <div className="p-2 bg-white/20 rounded-lg">
                                    <Edit className="w-6 h-6" />
                                </div>
                                <div className="flex flex-col justify-center">
                                    <CardTitle className="text-2xl">Bitácora de Visita</CardTitle>
                                    <CardDescription className="text-blue-100">
                                        Registra o actualiza los datos de tu bitácora
                                    </CardDescription>
                                </div>
                            </div>
                        </CardHeader>

                        {/* 🆕 INDICADOR DE CONEXIÓN */}
                        {!isOnline && (
                            <div className="bg-orange-500 text-white px-4 py-3">
                                <div className="flex items-center gap-2">
                                    <WifiOff className="h-5 w-5" />
                                    <p className="text-sm font-medium">
                                        Sin conexión - Modo offline activado. Los cambios se guardarán localmente.
                                    </p>
                                </div>
                            </div>
                        )}
                        <CardContent className="p-6 sm:p-8">
                            <div className="space-y-8">

                                {/* Información de fechas */}
                                {visits && (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">

                                        <div className="sm:col-span-2">
                                            <p className="text-sm text-muted-foreground">Organización</p>
                                            <p className="font-medium text-lg">{document.companyName}</p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-muted-foreground">Fecha de visita</p>
                                            <p className="font-medium">{visits.start.toLocaleDateString('es-ES', {
                                                weekday: 'long',
                                                year: 'numeric',
                                                month: 'long',
                                                day: 'numeric'
                                            })}</p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-muted-foreground">Horario</p>
                                            <p className="font-medium">
                                                {visits.start.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                                                {' - '}
                                                {visits.end.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                                            </p>
                                        </div>
                                    </div>
                                )}


                                {/* sInformación de la Visita */}
                                <section>
                                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2 text-slate-800 dark:text-slate-200">
                                        <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-blue-600 dark:text-blue-300 text-sm font-bold">
                                            1
                                        </div>
                                        Información de la Visita
                                    </h3>

                                    <div className="space-y-4">
                                        <div>
                                            <Label htmlFor="contactPerson" className="text-sm font-medium">
                                                Persona que recibe:
                                                <span className="text-red-500 ml-1">*</span>
                                            </Label>
                                            <Input
                                                id="contactPerson"
                                                value={contactPerson}
                                                onChange={(e) => setContactPerson(e.target.value)}
                                                placeholder="Ingresa el nombre de la persona"
                                                className="mt-1.5"
                                                required
                                            />
                                            {!contactPerson.trim() && (
                                                <p className="text-xs text-muted-foreground mt-1">
                                                    Este campo es obligatorio para guardar
                                                </p>
                                            )}
                                        </div>

                                        <div>
                                            <Label htmlFor="contactRole" className="text-sm font-medium">Cargo:</Label>
                                            <Input
                                                id="contactRole"
                                                value={contactRole}
                                                onChange={(e) => setContactRole(e.target.value)}
                                                className="mt-1.5" />
                                        </div>
                                    </div>
                                </section>



                                {/*  Acuerdos y Observaciones */}
                                <section>
                                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2 text-slate-800 dark:text-slate-200">
                                        <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-blue-600 dark:text-blue-300 text-sm font-bold">
                                            3
                                        </div>
                                        Acuerdos y Observaciones
                                    </h3>

                                    <div className="space-y-4">
                                        <div>
                                            <GenericListManager
                                                items={agreements}
                                                onChange={setAgreements}
                                                label="Acuerdos"
                                                placeholder="Describe el acuerdo..."
                                                singularLabel="Nuevo acuerdo"
                                                pluralLabel="acuerdos" />
                                        </div>

                                        <div>
                                            <GenericListManager
                                                items={observations}
                                                onChange={setObservations}
                                                label="Observaciones"
                                                placeholder="Describe las observaciones..."
                                                singularLabel="Nueva observación"
                                                pluralLabel="observaciones" />
                                        </div>
                                    </div>
                                </section>

                                {/* Firma */}
                                <section>
                                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2 text-slate-800 dark:text-slate-200">
                                        <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-blue-600 dark:text-blue-300 text-sm font-bold">
                                            4
                                        </div>
                                        Firma del Visitante
                                    </h3>

                                    <div className="space-y-4">
                                        <div>
                                            <Label className="text-sm font-medium">Actualizar Firma</Label>
                                            <div className="mt-1.5">
                                                <SignatureCapture
                                                    onSignatureSaved={() => setHasNewSignature(true)}
                                                    onSignaturePreview={(dataURL) => {
                                                        setSignaturePreview(dataURL);
                                                        setHasNewSignature(true);
                                                    }}

                                                />
                                            </div>

                                            {signaturePreview && (
                                                <div className="border rounded-lg p-2 bg-yellow-50 mt-2">
                                                    <p className="text-sm text-yellow-800 mb-2">Nueva firma (pendiente de guardar)</p>
                                                    <img src={signaturePreview} alt="Nueva firma" className="max-h-32 w-full object-contain mx-auto" />
                                                </div>
                                            )}
                                        </div>

                                        {/* Firma actual */}
                                        {signature && signature.fileId && (
                                            <div className="border rounded-lg p-4 bg-slate-50 dark:bg-slate-800">
                                                <p className="text-sm font-medium mb-2">Firma actual del receptor</p>
                                                <p className="text-xs text-muted-foreground mb-3">
                                                    Firmado el {signature.timestamp.toLocaleString('es-ES')}
                                                </p>
                                                <div className="border rounded-lg p-4 bg-white">
                                                    <img
                                                        src={RequestVisitService.getSignatureUrl(signature.fileId)}
                                                        alt="Firma"
                                                        className="max-h-48 mx-auto"
                                                    />
                                                </div>

                                                {document.signature[1] && (
                                                    <div className="mt-3">
                                                        {/* <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                                                            <MapPin className="h-4 w-4" />
                                                            <span>Coordenadas: {document.signature[1]}</span>
                                                        </div> */}

                                                        <Dialog>
                                                            <DialogTrigger asChild>
                                                                <Button variant="outline" size="sm" className="w-full">
                                                                    <MapPin className="h-4 w-4 mr-2" />
                                                                    Ver Ubicación en Mapa
                                                                </Button>
                                                            </DialogTrigger>

                                                            <DialogContent className="w-full max-w-2xl">
                                                                <DialogHeader>
                                                                    <DialogTitle>Ubicación de la Firma</DialogTitle>
                                                                    <DialogDescription>
                                                                        Coordenadas: <span className="text-blue-500">
                                                                            {document.signature[1]}
                                                                        </span>
                                                                    </DialogDescription>
                                                                </DialogHeader>
                                                                <MapboxMap
                                                                    coords={parseCoords(document.signature[1])}
                                                                    height="h-96"
                                                                />
                                                            </DialogContent>
                                                        </Dialog>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </section>
                            </div>

                            {/* Botones de Acción */}
                            <div className="flex flex-col sm:flex-row gap-3 mt-8 pt-6 border-t">
                                <Button
                                    onClick={handleUpdate}
                                    disabled={saving}
                                    className="flex-1 bg-blue-500 hover:bg-blue-600 cursor-pointer text-white font-medium"
                                >
                                    {saving ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            Guardando...
                                        </>
                                    ) : (
                                        <>
                                            <Save className="mr-2 h-4 w-4" />
                                            Guardar Cambios
                                        </>
                                    )}
                                </Button>

                                {/* <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                        <Button variant="destructive" className="flex-1 sm:flex-none sm:px-8 cursor-pointer">
                                            <Trash2 className="mr-2 h-4 w-4" />
                                            Eliminar
                                        </Button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent>
                                        <AlertDialogHeader>
                                            <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
                                            <AlertDialogDescription>
                                                Esta acción no se puede deshacer. Se eliminará permanentemente este registro.
                                            </AlertDialogDescription>
                                        </AlertDialogHeader>
                                        <AlertDialogFooter>
                                            <AlertDialogCancel className='cursor-pointer'>Cancelar</AlertDialogCancel>
                                            <AlertDialogAction onClick={handleDelete} className="bg-destructive cursor-pointer">
                                                Eliminar
                                            </AlertDialogAction>
                                        </AlertDialogFooter>
                                    </AlertDialogContent>
                                </AlertDialog> */}

                                <Button
                                    variant="outline"
                                    className="flex-1 sm:flex-none sm:px-8 cursor-pointer"
                                    onClick={() => {
                                        if (!userId) return;

                                        navigate({
                                            to: '/visitLog/$id',
                                            params: { id: userId },
                                        });
                                    }}
                                >
                                    <ArrowLeft className="mr-2 h-4 w-4" />
                                    Atrás
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </>
    );
};

export default AddVisitlog;